import React, { useEffect, useState } from "react";
import { Grid, Message } from "semantic-ui-react";
import { Link } from "../../../shims/useNavigate";
import Case from "case";

import { useTeamContext } from "../../../contexts/TeamContext";
import { useRestClientContext } from "../../../contexts/RestClientContext";

import Button from "../../UI/Button/Button";
import Card from "../../UI/Card/Card";
import EpcRatingTile from "../../UI/EpcRatingTile/EpcRatingTile";
import Preloader from "../../UI/Preloader/Preloader";

import "./YourProperties.scss";
import { Accessor } from "../../../types/types";
import { compressDataTableState } from "../../../hooks/useQueryFromState";
import { ColumnSort } from "@tanstack/react-table";

// TODO: Looks like this is referred to as Leaderboards on the server, would be nice to standardise this please

interface LeaderboardProperty {
	id: number;
	epc_rating: number;
	epc_rating_grade: string;
	addr_no: string;
	addr_street: string;
	postcode: string;
}

interface BiggestImpactLeaderboardProperty extends LeaderboardProperty {
	epc_rating_grade: string;
	potential_epc_rating: number;
	potential_epc_rating_grade: string;
}

interface YourPropertiesResponse {
	worst_performers: Array<LeaderboardProperty>;
	best_performers: Array<LeaderboardProperty>;
	biggest_impact: Array<BiggestImpactLeaderboardProperty>;
}

const generateDataTableLink = (accessor: Accessor, desc: boolean) => {
	const columnOrder: ColumnSort = {
		id: accessor,
		desc: desc,
	};
	const queryString = compressDataTableState({ sorting: [columnOrder] });

	return `/epc-data?${queryString}`;
};

const YourProperties = () => {
	const [data, setData] = useState<YourPropertiesResponse>();
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");

	const team = useTeamContext();
	const client = useRestClientContext();

	useEffect(() => {
		setLoading(true);
		setError("");
		client
			.fetchLeaderboards(team.id)
			.then((response: Response) => response.json())
			.then((json: YourPropertiesResponse) => {
				setLoading(false);
				setData(json);
			})
			.catch((_) => {
				setLoading(false);
				setError("Error fetching properties");
			});
	}, [client, team.id]);

	return (
		<Card title="Your Properties" className="your-properties">
			{loading && <Preloader />}
			{error && <Message error>{error}</Message>}
			{data ? (
				<Grid padded="vertically">
					{["worst_performers", "best_performers"].map((key) => (
						<Grid.Column key={key}>
							<Grid divided="vertically">
								<Grid.Row>
									<Grid.Column>
										<h3>{Case.title(key)}</h3>
									</Grid.Column>
								</Grid.Row>
								{data?.[key as keyof YourPropertiesResponse].map((obj) => (
									<Grid.Row key={obj.id} style={{ paddingTop: 0, paddingBottom: 0 }}>
										<Grid.Column width={1}>
											<EpcRatingTile grade={obj.epc_rating_grade} compact />
										</Grid.Column>
										<Grid.Column width={3}>({obj.epc_rating})</Grid.Column>
										<Grid.Column width={8}>
											<Link to={`/epc-data/${obj.id}`} className="property">
												<h4 className={"property-link"}>
													{`${obj.addr_no} ${obj.addr_street} ${obj.postcode}`}
												</h4>
											</Link>
										</Grid.Column>
									</Grid.Row>
								))}
								{
									<Link to={generateDataTableLink("metrics.epc_rating", key === "best_performers")}>
										<Button variant="contained">View {Case.title(key)}</Button>
									</Link>
								}
							</Grid>
						</Grid.Column>
					))}
					{!!data?.biggest_impact.length && (
						<Grid.Column>
							<Grid divided="vertically">
								<Grid.Row>
									<Grid.Column>
										<h3>Biggest Impact</h3>
									</Grid.Column>
								</Grid.Row>
								{data?.biggest_impact.map((obj) => (
									<Grid.Row key={obj.id} style={{ paddingTop: 0, paddingBottom: 0 }}>
										<Grid.Column width={1}>
											<EpcRatingTile grade={obj.epc_rating_grade} compact />
										</Grid.Column>
										<Grid.Column width={1}>
											<EpcRatingTile grade={obj.potential_epc_rating_grade} compact />
										</Grid.Column>

										<Grid.Column
											width={2}
										>{`${obj.epc_rating_grade} (${obj.epc_rating})`}</Grid.Column>
										<Grid.Column width={3}>
											{`${obj.potential_epc_rating_grade} 
											(${obj.potential_epc_rating.toFixed(0)})`}
										</Grid.Column>
										<Grid.Column width={6}>
											<Link to={`/epc-data/${obj.id}`}>
												<h4 className={"property-link"}>
													{obj.addr_no} {obj.addr_street} {obj.postcode}
												</h4>
											</Link>
										</Grid.Column>
									</Grid.Row>
								))}
								{
									<Link to={generateDataTableLink("sap_data.potential_epc_rating_improvement", true)}>
										<Button variant="contained">View Biggest Impact</Button>
									</Link>
								}
							</Grid>
						</Grid.Column>
					)}
				</Grid>
			) : (
				<Message>No data</Message>
			)}
		</Card>
	);
};

export default YourProperties;
